// cutscene-view.ts — the in-mission CUTSCENE player DOM port (HTML/DOM UI shell plan,
// Phase 5 — kidfCutScene 1047, the "opening screens" the CutSceneAction trigger
// performs). Renders the canvas CutSceneForm (ui/cutscene.ts) over the live canvas at
// the forms.ini.pp geometry — a CENTERED 160×160 panel:
//   - BITMAP kidcBitmap 1002 (5 5 150 0): the cutscene image at the TOP, width 150
//     design / NATIVE height (the .rbm authored as a .png atlas entry, switched by an
//     <img NAME> tag);
//   - ECOMTEXT kidcMessage 1001 (5 51 150 70): the typed narration BELOW the image —
//     Layout() REFLOWS this box so its top = the image bottom + 1 design unit and its
//     bottom = the OK button top − 1 (it fills the space under whatever image is
//     current);
//   - BUTTON kidcOk 2000 (105 141 50 12) "More...": pages; on the LAST page dismisses.
//
// THE TYPEWRITER is DOM-TIMER driven (rAF on performance.now deltas) — NOT gtimm — so
// the narration animates while the SP sim is paused (the SAME crux as the E-Com:
// gtimm freezes under the story-modal pause). It REUSES the E-Com reveal machinery
// verbatim — parseEcomMessage/revealSegments (ecom-message.ts) for the speaker
// @A/@J/@O/@F coloring + names, the 40 cps + 0.5s per-speaker-start pause, tap-to-skip
// (ShowAll), the page-completes-then-button rule, and the shared 9-slice button.
//
// THE IMAGE SIZING (the bitmap-sizing bug from tasks/lessons.md 2026-06-17): POSITIONS
// are design-units × s (s = 2*gs), but a BITMAP's intrinsic size (GetSize cx,cy) is in
// GAME px (~2 game-px per design unit — the SAME 2× baked into s). So the image is
// sized game-px × gs (= s/2), NOT s, or it renders 2× too big; AND clamped to the
// BITMAP control's 150-design width (= 150*s px = 300*gs px) preserving aspect (the
// canvas BitmapControl blits 1:1 at GetSize() game-px presented × gs).
//
// Display-only: the seam (host.CutScene → CutScene() → env.domCutScene) carries no
// sim/command/wire change.

import type { ButtonArt } from '../title-art.ts';
import { parseEcomMessage, type ParsedEcomMessage } from './ecom-message.ts';
import { buildRevealFragment } from './ecom-view.ts';
import { parseCutScene, type CutSceneSegment } from './cutscene-message.ts';
import {
  COL_FG,
  TEXT_SHADOW,
  el,
  FONT_BODY,
  buttonWidthU,
  formScale,
  at,
  makeStageButton,
  type StageButtonSize,
} from '../chrome.ts';

// ui/ecom.ts:58-59 — the reveal tuning (chars/sec + the per-speaker start pause),
// shared with the E-Com (the cutscene reuses the EcomTextControl typewriter).
const kcchPerSec = 40;
const kctSpeechDelayMs = 500; // kctSpeechDelay = 50 hsecs = 0.5s

const FONT = FONT_BODY;
const shadow = TEXT_SHADOW;

// forms.ini kidfCutScene 1047 (FORM 0 0 160 160 center): BITMAP 1002 (5 5 150 0),
// ECOMTEXT 1001 (5 51 150 70), BUTTON 2000 (105 141 50 12). All design units.
const FORM = { w: 160, h: 160 };
const BITMAP = { x: 5, y: 5, w: 150 }; // native height (0 ⇒ the bitmap's own)
const OK = { x: 105, y: 141, w: 50, h: 12 };
// the Layout() reflow gap PcFromFc(1, gcxFullScreenFormDiv10): 1 design unit at the
// 160-wide display (div10=16 ⇒ trunc(1*16/16)=1).
const REFLOW_GAP = 1;

/** A cutscene image bitmap loaded by the host (the <img NAME> art), as a CSS url +
 *  its natural GAME-px size (GetSize). null ⇒ the image is hidden (no <img> yet, or
 *  the atlas lacks it — the canvas HideCutSceneBitmap path). */
export interface CutSceneImage {
  url: string;
  cx: number;
  cy: number;
}

export interface CutSceneViewDeps {
  /** the overlay container (created by the host; this view owns its contents). */
  root: HTMLElement;
  /** the raw cutscene text (already ExpandVars-expanded by the caller), with the
   *  inline <img NAME>/<snd N> tags + the @-speaker narration. */
  message: string;
  /** load a cutscene <img NAME> bitmap into a CSS url + its natural size; null when
   *  the atlas has no such bitmap (createTBitmap miss). Called once per distinct
   *  image name (cached by the host). */
  loadImage: (name: string) => CutSceneImage | null;
  /** the 9-slice button art for the OK/More button; absent ⇒ a CSS button. */
  buttonArt?: ButtonArt;
  /** play a GUI/cutscene sfx (wired to the host SoundMgr) — used for the button tap
   *  AND the <snd N> stingers. */
  playSfx?: (sfx: number) => void;
  /** dismiss → the host unmounts the overlay + resumes (done() → next()). Called when
   *  the LAST page's OK is pressed. */
  onClose: () => void;
}

/** One typewriter page: the image to show while it types + the parsed narration for
 *  the page (the segment's text, box-fit). `firstOnSegment` flags the page that opens
 *  a new image segment (so its <snd> stingers fire + the image swaps + the box
 *  reflows). */
interface CutScenePage {
  image: string | null;
  sfx: number[];
  parsed: ParsedEcomMessage;
  firstOnSegment: boolean;
}

export class CutSceneView {
  private readonly d: CutSceneViewDeps;
  /** the shared 9-slice button renderer (the small dialog fallback flavour). */
  private readonly btn: (label: string, on: () => void, size: StageButtonSize) => HTMLButtonElement;
  private readonly segments: CutSceneSegment[];
  /** the per-page list across ALL image segments (each segment's narration box-fit). */
  private pages: CutScenePage[] = [];
  private pageIdx = 0;
  /** the running reveal position (message chars revealed on the CURRENT page). */
  private revealed = 0;
  private lastT = 0;
  private speechDelayLeft = 0;
  private raf: number | null = null;
  /** the image currently shown (so a page that doesn't change it keeps it). */
  private currentImage: string | null = null;

  // the live DOM nodes the reveal updates (rebuilt per page / resize).
  private textEl: HTMLElement | null = null;
  private imgEl: HTMLImageElement | null = null;
  private okBtn: HTMLButtonElement | null = null;
  private stageEl: HTMLElement | null = null;
  /** images already loaded (name → CutSceneImage|null), cached so a resize/repage
   *  doesn't re-decode + so we can read a swapped image's native size for the reflow. */
  private readonly imgCache = new Map<string, CutSceneImage | null>();
  /** the <snd> sets already fired (by page index) — so a resize/repage replay or a
   *  ShowAll doesn't re-trigger a stinger. */
  private readonly sfxFired = new Set<number>();

  constructor(d: CutSceneViewDeps) {
    this.d = d;
    const mk = makeStageButton(d);
    this.btn = (label, on, size) => mk(label, on, size, { fallback: 'dialog' });
    this.segments = parseCutScene(d.message).segments;

    const s = d.root.style;
    // a fixed, full-window MODAL overlay above the HUD (z=20, the in-game modal layer)
    // — pointer-events:all so the canvas behind it gets nothing while up; centered.
    s.position = 'fixed';
    s.inset = '0';
    s.display = 'flex';
    s.zIndex = '20';
    s.color = COL_FG;
    s.fontFamily = FONT;
    s.userSelect = 'none';
    s.pointerEvents = 'all';
    s.overflow = 'hidden';
    s.alignItems = 'center';
    s.justifyContent = 'center';
    // a tap anywhere NOT on the OK button reveals the whole current page (ShowAll —
    // cutscene.ts OnPenEvent:245-262 / OnControlSelected:209-214).
    d.root.addEventListener('pointerdown', this.onBackdropTap);
    window.addEventListener('resize', this.onResize);
    this.render();
    this.start();
  }

  private readonly onResize = (): void => {
    this.render();
  };

  private readonly onBackdropTap = (e: PointerEvent): void => {
    if (this.okBtn !== null && e.target instanceof Node && this.okBtn.contains(e.target)) return;
    this.showAll();
  };

  /** reveal the whole current page instantly (EcomTextControl.ShowAll). */
  private showAll(): void {
    const page = this.pages[this.pageIdx];
    if (page === undefined) return;
    this.revealed = page.parsed.revealLength;
    this.speechDelayLeft = 0;
    this.paintReveal();
  }

  destroy(): void {
    if (this.raf !== null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    window.removeEventListener('resize', this.onResize);
    this.d.root.removeEventListener('pointerdown', this.onBackdropTap);
  }

  // ── the design stage (the forms.ini coordinate space × the magnify) ──────────────
  // Same fixed magnification family as the E-Com / objectives / pause-menu DOM forms
  // (chrome.ts formScale / at), at the cutscene form's 160×160 size.
  private scale(): number {
    return formScale();
  }

  /** look up (cached) the loaded image for a name; null name / atlas-miss ⇒ null. */
  private image(name: string | null): CutSceneImage | null {
    if (name === null) return null;
    if (this.imgCache.has(name)) return this.imgCache.get(name)!;
    const img = this.d.loadImage(name);
    this.imgCache.set(name, img);
    return img;
  }

  /** the image's RENDERED size in design units (so the text box reflows below it). The
   *  bitmap is in GAME px; rendered px = cx*gs / cy*gs (gs = s/2), CLAMPED so the width
   *  ≤ the 150-design BITMAP control (= 150 design = 300 game px) preserving aspect.
   *  Returns the design-unit {w,h} the image occupies, or null when hidden. */
  private imageDesignSize(name: string | null): { w: number; h: number } | null {
    const img = this.image(name);
    if (img === null || img.cx <= 0 || img.cy <= 0) return null;
    // design size = game px / 2 (the 2× baked into s); clamp the width to ≤ BITMAP.w.
    let wDesign = img.cx / 2;
    let hDesign = img.cy / 2;
    if (wDesign > BITMAP.w) {
      const k = BITMAP.w / wDesign;
      wDesign = BITMAP.w;
      hDesign *= k;
    }
    return { w: wDesign, h: hDesign };
  }

  /** the reflowed message box (design units) under the CURRENT image: top = image
   *  bottom + the Layout() gap (or the bitmap's design top when hidden), bottom = the
   *  OK button top − the gap (cutscene.ts Layout:180-206). */
  private boxRect(imageName: string | null): { x: number; y: number; w: number; h: number } {
    const sz = this.imageDesignSize(imageName);
    const bottom = sz === null ? BITMAP.y : BITMAP.y + sz.h;
    const top = bottom + REFLOW_GAP;
    const boxBottom = OK.y - REFLOW_GAP;
    return { x: BITMAP.x, y: top, w: BITMAP.w, h: Math.max(0, boxBottom - top) };
  }

  /** build the (whole) overlay DOM for the CURRENT page; called on first show, on
   *  resize, and on a page advance. The reveal then paints into this.textEl. */
  render(): void {
    const s = this.scale();
    const page = this.pages[this.pageIdx] as CutScenePage | undefined;
    // the image this page shows (a page that doesn't change it inherits currentImage).
    const imageName = page?.image ?? this.currentImage;
    const stage = el('div', {
      position: 'relative',
      width: `${FORM.w * s}px`,
      height: `${FORM.h * s}px`,
      backgroundColor: 'rgb(18, 30, 33)',
    });
    stage.dataset.cutsceneStage = '1';

    // the reflowed message text box (top = image bottom + gap). Built + appended FIRST
    // so repaginate() can DOM-measure against it (the page set, hence the current
    // page's image, is then known). The typewriter renders the narration segments here.
    const box = el('div', {
      position: 'absolute',
      fontFamily: FONT,
      fontSize: `${textPx(s)}px`,
      lineHeight: '1.3',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      overflow: 'hidden',
      textShadow: shadow,
      // the box WIDTH is fixed (the BITMAP control width, 150 design) so repaginate()'s
      // scrollHeight measurement reflects the real wrap; the height is unconstrained
      // during measurement, then set to the reflow below.
      left: `${BITMAP.x * s}px`,
      width: `${BITMAP.w * s}px`,
    });
    box.dataset.cutsceneText = '1';
    this.textEl = box;
    stage.append(box);
    this.stageEl = stage;
    this.d.root.replaceChildren(stage);

    // (re)page the cutscene to fit the reflowed box heights — must run AFTER the box is
    // in the DOM to measure. The page set fixes the current page (hence its image).
    this.repaginate(s);

    // resolve the image this page shows now that the pages exist (a page that doesn't
    // change it inherits currentImage). On the FIRST render currentImage is null, so
    // the page's own image is what surfaces.
    const resolvedImage = this.pages[this.pageIdx]?.image ?? imageName;

    // the cutscene IMAGE at the top, sized game-px × gs (= s/2) clamped to ≤150 design
    // wide preserving aspect — NOT × s (that renders 2× too big), per the lesson.
    const sz = this.imageDesignSize(resolvedImage);
    const img = this.image(resolvedImage);
    if (sz !== null && img !== null) {
      const imgEl = el('img', {
        position: 'absolute',
        imageRendering: 'pixelated',
        left: `${BITMAP.x * s}px`,
        top: `${BITMAP.y * s}px`,
        width: `${sz.w * s}px`,
        height: `${sz.h * s}px`,
      }) as HTMLImageElement;
      imgEl.src = img.url;
      imgEl.dataset.cutsceneImg = '1';
      this.imgEl = imgEl;
      stage.append(imgEl);
    } else {
      this.imgEl = null;
    }

    // position the box at the reflow under the (now resolved) image: top = image bottom
    // + gap, bottom = the OK button top − gap (cutscene.ts Layout:180-206).
    const br = this.boxRect(resolvedImage);
    at(box, s, br.x, br.y, br.w, br.h);

    // the OK / More… button (the 9-slice frame + a WEB label) — hidden until the
    // current page is fully revealed. "More…" while pages remain, else "OK". SIZED to its
    // label via the shared buttonWidthU (the wide WI face filled the fixed 50u box), then
    // RIGHT-anchored at the canvas right edge (OK.x + OK.w = 155) so it keeps clear frame margin.
    const more = this.pageIdx < this.pages.length - 1;
    const label = more ? 'More…' : 'OK';
    const okW = buttonWidthU(label, OK.h, s);
    const okX = OK.x + OK.w - okW;
    const btn = this.btn(label, () => this.onOk(), { w: okW * s, h: OK.h * s });
    btn.dataset.cutsceneOk = '1';
    btn.style.visibility = 'hidden'; // appears when the page completes
    this.okBtn = btn;
    stage.append(at(btn, s, okX, OK.y, okW, OK.h));

    this.paintReveal();
  }

  /** OK/More pressed: advance to the next page (re-animate) or dismiss on the last. */
  private onOk(): void {
    if (this.pageIdx < this.pages.length - 1) {
      this.pageIdx++;
      this.revealed = 0;
      this.lastT = performance.now();
      this.speechDelayLeft = this.firstSpeechDelay(this.pages[this.pageIdx]?.parsed);
      this.render(); // rebuild for the new page (image may swap, box reflows)
      this.start(); // re-animate the new page
    } else {
      this.destroy();
      this.d.onClose();
    }
  }

  // ── paging (cutscene.ts More:104-177) ────────────────────────────────────────────
  // Build the per-page list across ALL image segments. The canvas More() emits text up
  // to the next <img> as a page, box-fitting via Font::CalcBreak; here, for the CURRENT
  // box geometry, we box-fit EACH image segment's narration into pages by DOM
  // measurement (the same greedy line-fill the E-Com paging uses). Each image segment's
  // box height is measured under THAT segment's image (the reflow). The page set is
  // rebuilt on a resize / an image swap (preserving the page index where possible).
  private repaginate(s: number): void {
    const text = this.textEl;
    if (text === null) {
      this.pages = this.flatPagesFallback();
      return;
    }

    const pages: CutScenePage[] = [];
    for (const seg of this.segments) {
      // the box height under THIS segment's image (the reflow). Measure against it.
      const boxH = this.boxRect(seg.image).h * s;
      const full = seg.text;

      const fits = (slice: string): boolean => {
        text.textContent = slice;
        return text.scrollHeight <= boxH + 0.5;
      };

      let chunks: string[];
      if (full.length === 0 || boxH <= 0 || fits(full)) {
        chunks = [full];
      } else {
        chunks = [];
        let start = 0;
        while (start < full.length) {
          let lo = start + 1,
            hi = full.length,
            best = start + 1;
          while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            if (fits(full.slice(start, mid))) {
              best = mid;
              lo = mid + 1;
            } else hi = mid - 1;
          }
          let brk = best;
          if (best < full.length) {
            const ws = full.lastIndexOf(' ', best - 1);
            if (ws > start) brk = ws + 1;
          }
          chunks.push(full.slice(start, brk));
          start = brk;
        }
      }

      chunks.forEach((chunk, ci) => {
        pages.push({
          image: ci === 0 ? seg.image : null,
          sfx: ci === 0 ? seg.sfx : [],
          parsed: parseEcomMessage(chunk),
          firstOnSegment: ci === 0,
        });
      });
    }

    this.pages = pages.length === 0 ? this.flatPagesFallback() : pages;
    if (this.pageIdx >= this.pages.length) this.pageIdx = this.pages.length - 1;
    if (this.pageIdx < 0) this.pageIdx = 0;
    // re-clamp the running reveal to the (possibly re-fit) current page: a resize changes
    // the scale, which can re-split the segment into fewer/more pages, so a stale revealed
    // > the new page's revealLength would short-circuit tick() and skip the typewriter.
    const cur = this.pages[this.pageIdx];
    if (cur !== undefined && this.revealed > cur.parsed.revealLength)
      this.revealed = cur.parsed.revealLength;
    text.textContent = '';
  }

  /** a no-measurement fallback (no DOM box): one page per image segment. */
  private flatPagesFallback(): CutScenePage[] {
    if (this.segments.length === 0) {
      return [{ image: null, sfx: [], parsed: parseEcomMessage(''), firstOnSegment: true }];
    }
    return this.segments.map((seg) => ({
      image: seg.image,
      sfx: seg.sfx,
      parsed: parseEcomMessage(seg.text),
      firstOnSegment: true,
    }));
  }

  // ── the DOM-timer typewriter (verbatim the E-Com reveal) ─────────────────────────
  private start(): void {
    if (this.raf !== null) cancelAnimationFrame(this.raf);
    this.lastT = performance.now();
    // apply this page's side effects: swap the current image + fire any <snd> stingers
    // ONCE (a re-render/resize that re-runs start must not re-trigger them).
    const page = this.pages[this.pageIdx];
    if (page !== undefined) {
      if (page.image !== null) this.currentImage = page.image;
      if (page.firstOnSegment && page.sfx.length > 0 && !this.sfxFired.has(this.pageIdx)) {
        this.sfxFired.add(this.pageIdx);
        for (const sfx of page.sfx) this.d.playSfx?.(sfx);
      }
    }
    if (this.speechDelayLeft === 0 && this.revealed === 0) {
      this.speechDelayLeft = this.firstSpeechDelay(page?.parsed);
    }
    const step = (now: number): void => {
      this.raf = null;
      this.tick(now);
      const p = this.pages[this.pageIdx];
      if (p !== undefined && this.revealed < p.parsed.revealLength) {
        this.raf = requestAnimationFrame(step);
      } else {
        this.paintReveal(); // ensure the button shows on completion
      }
    };
    this.raf = requestAnimationFrame(step);
  }

  private firstSpeechDelay(page: ParsedEcomMessage | undefined): number {
    if (page === undefined) return 0;
    const first = page.segments[0];
    return first !== undefined && first.name !== '' ? kctSpeechDelayMs : 0;
  }

  private tick(now: number): void {
    const page = this.pages[this.pageIdx];
    if (page === undefined || this.revealed >= page.parsed.revealLength) return;
    let dt = now - this.lastT;

    if (this.speechDelayLeft > 0) {
      const spend = Math.min(this.speechDelayLeft, dt);
      this.speechDelayLeft -= spend;
      dt -= spend;
      this.lastT = now;
      if (this.speechDelayLeft > 0) return;
    }

    const cch = Math.trunc((kcchPerSec * dt) / 1000);
    if (cch <= 0) return;
    this.lastT = now;

    const before = this.revealed;
    let target = Math.min(page.parsed.revealLength, this.revealed + cch);
    const nextBoundary = this.nextSpeakerBoundary(page.parsed, this.revealed);
    if (nextBoundary !== null && target > nextBoundary) target = nextBoundary;
    this.revealed = target;
    if (nextBoundary !== null && this.revealed === nextBoundary && this.revealed > before) {
      this.speechDelayLeft = kctSpeechDelayMs;
    }
    this.paintReveal();
  }

  private nextSpeakerBoundary(page: ParsedEcomMessage, pos: number): number | null {
    let start = 0;
    for (let i = 0; i < page.segments.length; i++) {
      const seg = page.segments[i];
      if (i > 0 && seg.name !== '' && start > pos) return start;
      start += seg.text.length;
    }
    return null;
  }

  private paintReveal(): void {
    const text = this.textEl;
    const page = this.pages[this.pageIdx];
    if (text === null || page === undefined) return;
    // lay out the WHOLE narration page every frame (unrevealed chars merely invisible) so
    // the wrapping is fixed and the streaming text never reflows — see buildRevealFragment.
    text.replaceChildren(buildRevealFragment(page.parsed, this.revealed));
    if (this.okBtn !== null) {
      this.okBtn.style.visibility =
        this.revealed >= page.parsed.revealLength ? 'visible' : 'hidden';
    }
  }
}

// the message-box font size in CSS px for a design scale (shared with the E-Com box).
function textPx(s: number): number {
  return Math.max(7, Math.round(6 * s));
}
